/**
 * BluetoothPrinterFields — Conexión Bluetooth SPP de la impresora térmica
 * (desktop: puerto COM; APK: dispositivos emparejados nativos).
 *
 * QUÉ EXPORTA: BluetoothPrinterFields (named).
 */

import { Bluetooth, Loader2, RefreshCw } from "lucide-react";
import type { PosPrinterConfig } from "@/lib/native/posPrinterConfig";
import { POS_HARDWARE_SECTION_LABEL } from "../utils/userSettingsPosHardware.helpers";

interface BluetoothPrinterFieldsProps {
  cfg: PosPrinterConfig;
  onChange: (patch: Partial<PosPrinterConfig>) => void;
  isDesktop: boolean;
  comPorts: string[];
  comLoading: boolean;
  onRefreshComPorts: () => void;
  btDevices: { name: string; address: string }[];
  btLoading: boolean;
  onRefreshBluetooth: () => void;
}

export function BluetoothPrinterFields({
  cfg,
  onChange,
  isDesktop,
  comPorts,
  comLoading,
  onRefreshComPorts,
  btDevices,
  btLoading,
  onRefreshBluetooth,
}: BluetoothPrinterFieldsProps) {
  return (
    <div className="space-y-3">
      {isDesktop ? (
        /* Desktop: BT emparejado = puerto COM virtual en Windows */
        <>
          <div className="flex items-center justify-between gap-2">
            <p className={POS_HARDWARE_SECTION_LABEL}>
              Puertos COM disponibles
            </p>
            <button
              type="button"
              onClick={onRefreshComPorts}
              disabled={comLoading}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-primary-600 text-white text-[10px] font-bold disabled:opacity-50"
            >
              {comLoading ? (
                <Loader2 size={12} className="animate-spin" />
              ) : (
                <RefreshCw size={12} />
              )}
              Buscar
            </button>
          </div>
          <div className="flex items-start gap-2 p-2.5 rounded-xl bg-blue-50/90 dark:bg-blue-950/30 border border-blue-200/70 dark:border-blue-900/50">
            <Bluetooth
              size={14}
              className="text-blue-600 dark:text-blue-400 shrink-0 mt-0.5"
            />
            <p className="text-[10px] text-blue-900 dark:text-blue-100 leading-snug">
              Empareja la impresora en <strong>Ajustes → Bluetooth</strong> de
              Windows, luego pulsa <strong>Buscar</strong>. El puerto COM
              emparejado (ej. <strong>COM3</strong>) aparecerá en la lista.
            </p>
          </div>
          {/* Lista de puertos COM descubiertos */}
          {comPorts.length > 0 && (
            <ul className="max-h-40 overflow-y-auto space-y-1 custom-scrollbar">
              {comPorts.map((port) => {
                const sel = cfg.bluetoothMac === port;
                return (
                  <li key={port}>
                    <button
                      type="button"
                      onClick={() => onChange({ bluetoothMac: port })}
                      className={`w-full text-left px-3 py-2.5 rounded-xl border text-xs font-semibold transition-all ${
                        sel
                          ? "border-primary-600 bg-primary-50 dark:bg-primary-950/40 text-primary-800 dark:text-primary-200"
                          : "border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-gray-800 dark:text-slate-100"
                      }`}
                    >
                      <span className="font-mono">{port}</span>
                      {sel && (
                        <span className="ml-2 text-[9px] font-black uppercase tracking-wide text-primary-600">
                          Seleccionado
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {comPorts.length === 0 && !comLoading && (
            <p className="text-[11px] text-gray-500 dark:text-slate-400 px-0.5">
              Pulsa «Buscar» con la impresora Bluetooth encendida y emparejada.
            </p>
          )}
          {/* Input manual como fallback */}
          <div className="relative">
            <Bluetooth
              size={16}
              className="abs-center-y left-3 text-gray-400"
            />
            <input
              type="text"
              autoComplete="off"
              placeholder="Puerto COM manual (ej. COM3)"
              value={cfg.bluetoothMac}
              onChange={(e) =>
                onChange({ bluetoothMac: e.target.value.trim().toUpperCase() })
              }
              className="w-full pl-10 pr-3 py-3 rounded-xl border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-xs font-mono"
            />
          </div>
        </>
      ) : (
        /* Android/iOS: escáner Bluetooth nativo */
        <>
          <div className="flex items-center justify-between gap-2">
            <p className="text-[9px] font-black uppercase tracking-widest text-gray-400 dark:text-slate-500">
              Dispositivos emparejados
            </p>
            <button
              type="button"
              onClick={onRefreshBluetooth}
              disabled={btLoading}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-primary-600 text-white text-[10px] font-bold disabled:opacity-50"
            >
              {btLoading ? (
                <Loader2 size={12} className="animate-spin" />
              ) : (
                <RefreshCw size={12} />
              )}
              Buscar
            </button>
          </div>
          <div className="flex items-start gap-2 p-2.5 rounded-xl bg-blue-50/90 dark:bg-blue-950/30 border border-blue-200/70 dark:border-blue-900/50">
            <Bluetooth
              size={14}
              className="text-blue-600 dark:text-blue-400 shrink-0 mt-0.5"
            />
            <p className="text-[10px] text-blue-900 dark:text-blue-100 leading-snug">
              En Android 12+ la app pedirá permiso{" "}
              <strong>Dispositivos cercanos / Bluetooth</strong>. Solo aparecen
              impresoras <strong>ya emparejadas</strong> en el sistema.
            </p>
          </div>
          {btDevices.length === 0 ? (
            <p className="text-[11px] text-gray-500 dark:text-slate-400 px-0.5">
              No se encontraron dispositivos. Pulsa «Buscar» o empareja la
              impresora en Ajustes. También puedes pegar la MAC manualmente
              abajo.
            </p>
          ) : (
            <ul className="max-h-40 overflow-y-auto space-y-1 custom-scrollbar">
              {btDevices.map((d) => {
                const sel = cfg.bluetoothMac === d.address;
                return (
                  <li key={d.address}>
                    <button
                      type="button"
                      onClick={() =>
                        onChange({
                          bluetoothMac: d.address,
                          deviceName: cfg.deviceName || d.name,
                        })
                      }
                      className={`w-full text-left px-3 py-2.5 rounded-xl border text-xs font-semibold transition-all ${
                        sel
                          ? "border-primary-600 bg-primary-50 dark:bg-primary-950/40 text-primary-800 dark:text-primary-200"
                          : "border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-gray-800 dark:text-slate-100"
                      }`}
                    >
                      <span className="block font-bold">{d.name}</span>
                      <span className="text-[10px] font-mono text-gray-500">
                        {d.address}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          <div className="relative">
            <Bluetooth
              size={16}
              className="abs-center-y left-3 text-gray-400"
            />
            <input
              type="text"
              autoComplete="off"
              placeholder="MAC manual (00:11:22:33:44:55)"
              value={cfg.bluetoothMac}
              onChange={(e) =>
                onChange({ bluetoothMac: e.target.value.trim().toUpperCase() })
              }
              className="w-full pl-10 pr-3 py-3 rounded-xl border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-xs font-mono"
            />
          </div>
        </>
      )}
    </div>
  );
}
